import {ActionTree} from 'vuex'
import {AuthState} from '@/modules/auth/store'
import {RootState} from '@/store/types'
import * as api from '@/modules/auth/api'
import {MutationTypes} from '@/modules/auth/store/mutation'
import JWTToken, {ActiveUser, User} from '@/modules/auth/types'

function updateActiveUsers (users: ActiveUser[]) {
    localStorage.setItem(
        'app.activeUsers',
        JSON.stringify(users.map(u => u.serialize())),
    )
}

const actions: ActionTree<AuthState, RootState> = {

    /**
     * Login
     *
     * Solicita al servidor acceso a los servicios autenticando con el usuario
     * y contraseña del payload.
     */
    login: async ({commit, dispatch}, payload: { user: string, password: string }) => {
        let token: JWTToken | string = await api.token(payload.user, payload.password)

        localStorage.setItem('token', token)

        // Registro del token en el estado
        token = new JWTToken(token)
        commit(MutationTypes.SetToken, {token})

        const user = new User(token)
        // Registro del usuario actual en el estado
        commit(MutationTypes.SetUser, user)
        const root = true

        // Ejecuta todas las tareas necesarias para el arranque de la aplicación
        // una vez el usuario se ha autenticado.
        // Los proceso son ejecutados de forma paralela mediante Promise.all
        await Promise.all(
            [
                dispatch('loadModules', {force: true}, {root}),
                dispatch('loadConfig', null, {root}),
                dispatch('addActiveUser', Object.assign(new ActiveUser(), {
                    id: user.id,
                    email: user.email,
                    name: user.name,
                    token: token.encoded,
                })),
            ],
        )

        commit(MutationTypes.ToggleChangingActiveUser, false)

        return token
    },

    /**
     * Cerrar sesión
     *
     * Elimina el token de autorización actual
     */
    logout: async ({state, commit, dispatch}) => {
        const user = state.user!
        // Añade el token actual a una lista negra, para que nunca mas se pueda
        // re-utilizar
        await api.addTokenToBlacklist(state.token!)

        // Limpia la configuración de acceso a módulos
        await dispatch('clearCurrentAuth')
        await dispatch('clearModuleConfig', null, {root: true})
        await dispatch('authorization/clearStatus', null, {root: true})
    },

    /**
     * Cargar el token de autorización actual de la aplicación
     */
    loadToken: ({commit, state}, payload: {force?: boolean} = {}): JWTToken | null => {
        if (state.token && !payload.force) {
            return state.token
        }

        // Si el token no esta cargado en el estado se intenta cargar desde el
        // localStorage
        const encoded = localStorage.getItem('token')
        if (encoded) {
            const token = new JWTToken(encoded)
            commit(MutationTypes.SetToken, {token})
            commit(MutationTypes.SetUser, new User(token))

            return token
        }

        return null
    },

    /**
     * Carga los usuarios activos de la aplicación
     * @param state
     * @param commit
     */
    async loadActiveUsers ({state, commit}) {
        const data = JSON.parse(
            localStorage.getItem('app.activeUsers') || '[]',
        )

        // No se ha podido cargar los usuario activos
        if (!Array.isArray(data)) {
            return
        }

        const activeUsers = data.map(u => ActiveUser.deserialize(u))
        // Si el usuario actual no existe en la lista de usuarios guardados, se añade
        const user = state.user
        if (user && !activeUsers.find(u => u.id === user!.id)) {
            activeUsers.push(Object.assign(new ActiveUser(), {
                id: user.id,
                name: user.name,
                email: user.email,
                token: state.token!.encoded,
            }))

            updateActiveUsers(state.activeUsers)
        }

        commit(MutationTypes.SetActiveUsers, activeUsers)
    },

    async addActiveUser ({state, commit}, user: ActiveUser) {
        commit(MutationTypes.AddActiveUser, user)
        updateActiveUsers(state.activeUsers)
    },

    async removeActiveUser ({state, commit}, user: ActiveUser) {
        if (user.token) {
            throw new Error('No se puede eliminar un usuario activo que posee token, primero debe cerrar sesión')
        }

        commit(MutationTypes.RemoveActiveUser, user.id)
        updateActiveUsers(state.activeUsers)
    },

    /**
     * Limpia la autenticación actual del estado.
     *
     * - Elimina del estado el token JWT actual
     * - Elimina del estado el usuario asociado al token
     * - Elimina el token del usuario guardado para inicio de sesión rápido
     *
     * @param state
     * @param commit
     * @param id
     */
    clearCurrentAuth ({state, commit}) {
        const id = state.user!.id
        // Elimina el token del estado de la aplicación y del navegador del usuario
        localStorage.removeItem('token')
        commit(MutationTypes.SetToken, {token: null})
        commit(MutationTypes.SetUser, null)

        // Elimina el token del usuario actual
        commit(MutationTypes.UpdateActiveUserField, {id, field: 'token', value: ''})
        updateActiveUsers(state.activeUsers)
    },

    /**
     * Cambia el usuario actual de la aplicación por uno de los usuarios activos guardados
     *
     * @param dispatch
     * @param user
     */
    async changeActiveUser ({dispatch}, user: ActiveUser) {
        if (!user.token) {
            throw new Error('El usuario no tiene token')
        }

        localStorage.setItem('token', user.token)
        // Fuerza a la aplicación a recargar el token desde el localStorage
        await dispatch('loadToken', {force: true})

        const root = true
        await dispatch('loadModules', {force: true}, {root})
        await dispatch('loadConfig', null, {root})
    },
}

export default actions
